import React from 'react';
import PropTypes from 'prop-types';
import * as PeoplePreStyles from '../../styles/internal-page/contact-card.module.scss';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { useTranslation } from 'react-i18next';
import { sortAffiliations } from '../../utils/parsing';
import avatar from '../../static/images/avatar-web.jpg';
import PolyLink from './PolyLink';
import clsx from 'clsx';

const formatDate = (dateString, language) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString(language, { month: 'long' });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const PeoplePre = ({
  researcher
}) => {
  const {t, i18n} = useTranslation();
  const affiliat = sortAffiliations(researcher.affiliation);
  const mobiles = researcher.phone?.filter((e) => e.type === 'MOBILE');
  const phones = researcher.phone?.filter((e) => e.type === 'PHONE');
  const isFeminine = researcher.title === 'FEMININ';
  const displayAffiliation = (researcher, affiliat, t) => {
    const elements = [];
    if(affiliat[0].academic_rank
      && (isFeminine ? affiliat[0].academic_rank.label_f : affiliat[0].academic_rank.label)
    ) {
      elements.push(
        <span key="academic">
          {isFeminine ? affiliat[0].academic_rank.label_f : affiliat[0].academic_rank.label}
          {affiliat[0].secondment && (
            <span className={PeoplePreStyles.detach}>
              {' ' + t('research.people_pre.detach')}
            </span>
          )}
        </span>
      );
    }
    if(researcher.HDR) {
      elements.push(<span key="hdr">HDR</span>);
    }
    if(affiliat[0].administrative_rank && (isFeminine
      ? affiliat[0].administrative_rank.label_f
      : affiliat[0].administrative_rank.label)
    ) {
      elements.push(
        <span key="admin0">{isFeminine ? affiliat[0].administrative_rank.label_f : affiliat[0].administrative_rank.label}</span>
      );
    }
    affiliat.slice(1).forEach((affil, index) => {
      if((isFeminine
        ? affil?.administrative_rank?.label_f : affil?.administrative_rank?.label)
      ) {
        elements.push(
          <span key={`admin${index + 1}`}>
            {isFeminine ? affil?.administrative_rank?.label_f : affil?.administrative_rank?.label}
          </span>
        );
      }
    });

    return elements.reduce((prev, curr, i) => {
      return i === 0 ? [curr] : [...prev, ', ', curr];
    }, []);
  };
  return (
    <div>
      <div className={PeoplePreStyles.root}>
        {researcher.publishable_photo && researcher.photo ?
          <div>
            <GatsbyImage
              alt=""
              className={PeoplePreStyles.profileImage}
              image={getImage(researcher.photo.imageFile)}
              loading="eager"
              role={'none'}
            />
          </div>
          :
          <img
            alt=""
            className={PeoplePreStyles.profileImage}
            loading="eager"
            role={'none'}
            src={avatar}
          />
        }
        <div className={PeoplePreStyles.infoContainer}>
          <h1 className={PeoplePreStyles.name}>
            {researcher.firstname + ' ' + researcher.lastname}
          </h1>
          <div className={PeoplePreStyles.underline}></div>
          <p className={PeoplePreStyles.title}>
            {affiliat[0] &&
              displayAffiliation(researcher, affiliat, t)
            }
          </p>
          {affiliat.filter((e) => e.organization).length > 0 &&
            <p className={PeoplePreStyles.organization}>
              {affiliat.filter((e) => e.organization).map((e, i) => (
                e.organization.name + (affiliat[i + 1] ? ', ' : '')
              ))}
            </p>
          }
          <p className={PeoplePreStyles.affliation}>
            {researcher.affiliation_external}
          </p>
          <div>
            {affiliat && affiliat.length > 0 && affiliat[0].start_date && affiliat[0].end_date &&
              <p
                className={PeoplePreStyles.number}
                key="date-affiliation"
              >
                <span className={PeoplePreStyles.office}>{t('research.people_pre.affiliationDuration')}</span>
                <span>{t('research.people_pre.affiliationPeriod', {
                  startDate: formatDate(affiliat[0].start_date, i18n.language),
                  endDate: formatDate(affiliat[0].end_date, i18n.language)
                })}
                </span>
              </p>
            }
            <a
              className={PeoplePreStyles.email}
              href={`mailto:${researcher.email}`}
            >
              {researcher.email}
            </a>
            {phones && phones.length > 0 &&
              phones?.map((phone, index) => (
                phone.publishable &&
                <p
                  className={PeoplePreStyles.number}
                  key={`${index}-phone`}
                >
                  <span className={PeoplePreStyles.office}>{t('research.people_pre.fixe')}</span>
                  <span>{phone.number}</span>
                </p>
              ))
            }
            {mobiles && mobiles.length > 0 &&
              mobiles?.map((mobile, index) => (
                mobile.publishable &&
                <p
                  className={PeoplePreStyles.number}
                  key={`${index}-mobile`}
                >
                  <span className={PeoplePreStyles.office}>{t('research.people_pre.mobile')}</span>
                  <span>{mobile.number}</span>
                </p>
              ))
            }
            {researcher.office_number &&
              <p className={PeoplePreStyles.adress}>
                <span className={PeoplePreStyles.office}>{t('research.people_pre.office')}</span>{researcher.office_number}
              </p>
            }
          </div>
        </div>
      </div>
      {(researcher.themes
        || (researcher.disciplines && researcher.disciplines.length > 0)
        || (researcher.subdisciplines && researcher.subdisciplines.length > 0)
        || (researcher.research_axes && researcher.research_axes.length > 0)
        || (researcher.geographical_areas && researcher.geographical_areas.length > 0)
        || (researcher.countries && researcher.countries.length > 0)
        || (researcher.languages && researcher.languages.length > 0)
      ) && (
        <div className={PeoplePreStyles.searchContainer}>
          {researcher.themes &&
            <p className={PeoplePreStyles.containerAll}>
              <span className={PeoplePreStyles.theme}>
                {t('research.people_pre.theme')}
              </span>
              {researcher.themes}
            </p>
          }
          {researcher.disciplines && researcher.disciplines.length > 0 &&
          <p className={PeoplePreStyles.containerAll}>
            <span className={PeoplePreStyles.theme}>
              {t('research.people_pre.disciplines')}
            </span>
            <span>
              {researcher.disciplines.map((e, i) => (
                <span key={'disc' + i}>
                  {e.code + (researcher.disciplines[i + 1] ? ', ' : '')}
                </span>
              ))}
            </span>
          </p>
          }
          {researcher.subdisciplines && researcher.subdisciplines.length > 0 &&
          <p className={PeoplePreStyles.containerAll}>
            <span className={PeoplePreStyles.theme}>
              {t('research.people_pre.subdisciplines')}
            </span>
            <span>
              {researcher.subdisciplines.map((e, i) => (
                <span key={'subdisc' + i}>
                  {e.code + (researcher.subdisciplines[i + 1] ? ', ' : '')}
                </span>
              ))}
            </span>
          </p>
          }
          {researcher.research_axes && researcher.research_axes.length > 0 &&
          <p className={PeoplePreStyles.containerAll}>
            <span className={PeoplePreStyles.theme}>
              {t('research.people_pre.axeresearchlower')}
            </span>
            <span>
              {researcher.research_axes?.map((e, i) => (
                <>
                  <PolyLink
                    className={clsx({[PeoplePreStyles.axeLink]: e.url})}
                    key={'axe' + i}
                    rel="noreferrer"
                    url={e.url}
                  >
                    {e.code}
                  </PolyLink>
                  {researcher.research_axes[i + 1] ? ', ' : ''}
                </>
              ))}
            </span>
          </p>
          }
          {researcher.geographical_areas && researcher.geographical_areas.length > 0 &&
          <p className={PeoplePreStyles.containerAll}>
            <span className={PeoplePreStyles.theme}>
              {t('research.people_pre.geoarea')}
            </span>
            <span>
              {researcher.geographical_areas?.map((e, i) => (
                <span key={'geoarea' + i}>
                  {e.area + (researcher.geographical_areas[i + 1] ? ', ' : '')}
                </span>
              ))}
            </span>
          </p>
          }
          {researcher.countries && researcher.countries.length > 0 &&
          <p className={PeoplePreStyles.containerAll}>
            <span className={PeoplePreStyles.theme}>
              {t('research.people_pre.countries')}
            </span>
            {researcher.countries.map((e, i) => (
              <span key={'countries' + i}>
                {e.name + (researcher.countries[i + 1] ? ', ' : '')}
              </span>
            ))}
          </p>
          }
          {researcher.languages && researcher.languages.length > 0 &&
          <p className={PeoplePreStyles.containerAll}>
            <span className={PeoplePreStyles.theme}>
              {t('research.people_pre.languages')}
            </span>
            {researcher.languages.map((e, i) => (
              <span key={'languages' + i}>
                {e.name + (researcher.languages[i + 1] ? ', ' : '')}
              </span>
            ))}
          </p>
          }
        </div>
      )}
    </div>
  );
};

PeoplePre.propTypes = {
  researcher: PropTypes.shape({
    id: PropTypes.string,
    affiliation_external: PropTypes.string,
    title: PropTypes.string,
    slug: PropTypes.string,
    cmsRsDirId: PropTypes.number,
    firstname: PropTypes.string,
    lastname: PropTypes.string,
    email: PropTypes.string,
    office_number: PropTypes.string,
    HDR: PropTypes.any,
    research_wip: PropTypes.string,
    thesis: PropTypes.any,
    thesis_wip: PropTypes.string,
    biography: PropTypes.string,
    publishable_photo: PropTypes.bool,
    publications: PropTypes.string,
    projects: PropTypes.any,
    teachings: PropTypes.string,
    edit_resps: PropTypes.any,
    awards: PropTypes.any,
    themes: PropTypes.any,
    teams: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    })),
    languages: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string
    })),
    research_axes: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string
    })),
    disciplines: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
      subdisciplines: PropTypes.arrayOf(PropTypes.shape({
        code: PropTypes.string
      }))
    })),
    subdisciplines: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
      discipline: PropTypes.shape({
        code: PropTypes.string
      })
    })),
    geographical_areas: PropTypes.arrayOf(PropTypes.shape({
      area: PropTypes.string
    })),
    affiliation: PropTypes.arrayOf(PropTypes.shape({
      start_date: PropTypes.string,
      end_date: PropTypes.string,
      secondment: PropTypes.bool,
      organization: PropTypes.shape({
        name: PropTypes.string
      }),
      academic_rank: PropTypes.shape({
        id: PropTypes.string,
        code: PropTypes.string,
        label: PropTypes.string,
        label_f:PropTypes.string
      }),
      administrative_rank: PropTypes.shape({
        id: PropTypes.string,
        code: PropTypes.string,
        label: PropTypes.string,
        label_f: PropTypes.string
      })
    })),
    identifier: PropTypes.any,
    phone: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      number: PropTypes.string,
      type: PropTypes.string,
      publishable: PropTypes.bool
    })),
    countries: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string
    })),
    photo: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      captionEN: PropTypes.string,
      caption: PropTypes.string,
      creator: PropTypes.string,
      alternativeTextEN: PropTypes.string,
      alternativeText: PropTypes.string,
      url: PropTypes.string,
      imageFile: PropTypes.shape({
        childImageSharp: PropTypes.shape({
          gatsbyImageData: PropTypes.object
        })
      })
    })
  })
};

export default PeoplePre;